import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { Dialog } from '../../../../../components/ui/Dialog';
import { DatePicker, DateTimePicker } from '../../../../../components/ui/DatePicker';
import { FieldHelp } from '../../../../../components/ui/FieldHelp';
import { Icon } from '../../../../../components/ui/Icon';
import { Input } from '../../../../../components/ui/Input';
import { Link } from '../../../../../components/ui/Link';
import { PageBreadcrumb, PageTabs } from '../../../../../components/ui/ModulePage';
import { Radio } from '../../../../../components/ui/Radio';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { Select } from '../../../../../components/ui/Select';
import { Switch } from '../../../../../components/ui/Switch';
import { Tag } from '../../../../../components/ui/Tag';
import { Textarea } from '../../../../../components/ui/Textarea';
import { demoToday, shiftDays } from '../../../../../lib/demoTime';
import teacherAvatarSrc from '../../../../../assets/teacher-avatars/teacher-1.png';
import defaultAvatarSrc from '../assets/default-avatar.png';
import { AiRecordDialog } from './AiRecordDialog';
import styles from './IntentionStudentDetail.module.css';

/*
 * 意向学员详情页（招生营销 / 意向学员管理）。
 *
 * 结构对齐参考截图：面包屑 → 返回行 → 学员头部卡（基本信息 + 客户状态/标签 + 操作按钮）→
 * 页签卡（沟通记录 / 试听记录 / 优惠券 / 意向课程 / 其他信息 / 覆盖记录 / 动态）。
 * 沟通记录页签接了完整的筛选 + 记录流演示，其余页签仍是占位。
 *
 * 后期「意向学员管理」上一层会加学员列表汇总页，返回链接届时指向列表页；
 * 演示阶段先返回上一级历史记录。
 */

/** 意向级别的心形总数，取值 0-5。 */
const INTENTION_HEART_COUNT = 5;

const receptionTeachers = [
  { value: '陈清和', label: '陈清和' },
  { value: '许星禾', label: '许星禾' },
] as const;

/** 详情页签；只有「沟通记录」接了内容，其余展示占位。 */
const detailTabs = [
  { id: 'communication', label: '沟通记录' },
  { id: 'audition', label: '试听记录' },
  { id: 'coupon', label: '优惠券' },
  { id: 'intention-course', label: '意向课程' },
  { id: 'other', label: '其他信息' },
  { id: 'coverage', label: '覆盖记录' },
  { id: 'moments', label: '动态' },
] as const;

type DetailTabId = (typeof detailTabs)[number]['id'];

/** 记录字段里需要特殊控件的三类：星级、到访开关、可编辑责任人；其余都是纯文本。 */
interface RecordField {
  /** 沟通内容这类说明性字段独占一整行。 */
  full?: boolean;
  kind?: 'stars' | 'switch' | 'edit';
  label: string;
  value?: string;
  /** 值较长（时间、说明文案）的字段横跨两列，避免被窄列挤断行。 */
  wide?: boolean;
}

/*
 * 原位编辑的表单数据（字段按线上环境对齐）：展示态记录与编辑表单共用这一份结构。
 * 「修改」进入编辑（表单以当前展示值初始化），「保存」把表单写回展示态，「取消」丢弃。
 */
interface CommunicationDraft {
  /** 客户状态；编辑态只读展示。 */
  customerStatus: string;
  /** 客户有效性：有效 / 无效 / 待定；编辑态只读展示。 */
  customerValidity: string;
  /** 意向级别，取值 0-5，编辑态可点。 */
  intentionLevel: number;
  /** 沟通情况（有效 / 无效沟通）。 */
  situation: string;
  /** 沟通类型；编辑态只读展示。 */
  commType: string;
  /** 承诺到访开关。 */
  promiseVisit: boolean;
  /** 诺到类型。 */
  visitType: string;
  /** 诺到日，格式 `YYYY-MM-DD HH:mm:ss`。 */
  visitDate: string;
  /** 接待教练。 */
  receptionTeacher: string;
  /** 承诺到访时的接待校区，仅用于本地模拟。 */
  visitCampus: string;
  /** 审核状态；编辑态只读展示，线上常为空。 */
  auditStatus: string;
  /** 下次跟进类型。 */
  nextFollowType: string;
  /** 下次跟进日期勾了「待定」时选择器禁用、展示态显示「待定」。 */
  nextFollowPending: boolean;
  /** 下次跟进日期。 */
  nextFollowDate: string;
  /** 沟通内容，上限 1000 字。 */
  remark: string;
}

const initialDraft: CommunicationDraft = {
  customerStatus: '需求强烈',
  customerValidity: '有效',
  intentionLevel: 3,
  situation: '有效沟通',
  commType: '电话',
  promiseVisit: true,
  visitType: '试听',
  visitDate: `${shiftDays(demoToday, -2)} 20:37:00`,
  receptionTeacher: '',
  visitCampus: '',
  auditStatus: '',
  nextFollowType: '',
  nextFollowPending: true,
  nextFollowDate: '',
  remark: '已与知夏妈妈沟通数学学习情况，家长希望先巩固基础，已约定周六上午到校试听。稍后发送课程安排、班额及费用说明，周五再次确认。',
};

/** 沟通记录的固定头部信息：演示只有一条记录，操作人与时间写死。 */
const recordOperator = '陈清和';
const recordTime = `${shiftDays(demoToday, -2)} 20:38`;

/* 意向级别用心形图标表示：已选中（level 以内）实心橙，未选中空心灰。 */
function IntentionHearts({ level }: { level: number }) {
  return (
    <span aria-label={`意向级别 ${level} 心`} className={styles.hearts} role="img">
      {Array.from({ length: INTENTION_HEART_COUNT }, (_, index) => (
        <Icon className={index < level ? styles.heartOn : styles.heartOff} key={index} name={index < level ? 'shoucangxuanzhong' : 'shoucang'} size="xs" />
      ))}
    </span>
  );
}

/* 编辑表单里的意向级别：心形可点击，点到第几颗就是几心。 */
function EditableHearts({ level, onChange }: { level: number; onChange: (level: number) => void }) {
  return (
    <span aria-label={`意向级别 ${level} 心`} className={styles.hearts} role="radiogroup">
      {Array.from({ length: INTENTION_HEART_COUNT }, (_, index) => (
        <button
          aria-label={`${index + 1} 心`}
          className={styles.heartButton}
          key={index}
          type="button"
          onClick={() => onChange(index + 1)}
        >
          <Icon className={index < level ? styles.heartOn : styles.heartOff} name={index < level ? 'shoucangxuanzhong' : 'shoucang'} size="xs" />
        </button>
      ))}
    </span>
  );
}

function FieldCell({ field, visited, onVisitChange, operator, showColon = true }: {
  field: RecordField;
  visited: boolean;
  onVisitChange: (visited: boolean) => void;
  operator: string;
  /** 展示字段默认保留冒号，与编辑态保持一致。 */
  showColon?: boolean;
}) {
  return (
    <div className={[styles.field, field.full ? styles.fieldFull : '', field.wide ? styles.fieldWide : ''].filter(Boolean).join(' ')}>
      <span className={styles.fieldLabel}>{field.label}{showColon ? ':' : ''}</span>
      {field.kind === 'stars' ? <IntentionHearts level={Number(field.value ?? 0)} />
        : field.kind === 'switch' ? <Switch checked={visited} label={`${operator}${field.label}`} onCheckedChange={onVisitChange} size="small" />
          : <>
            <span className={[styles.fieldValue, field.value ? '' : styles.fieldValueEmpty].filter(Boolean).join(' ')}>{field.value || '--'}</span>
            {field.kind === 'edit' ? <Link aria-label={`编辑${field.label}`} leadingIcon="xiugai1" onClick={() => {}}>{''}</Link> : null}
          </>}
    </div>
  );
}

/** 展开区的小节：蓝色竖条标题 + 四列字段网格。 */
function FieldSection({ title, fields }: { title: string; fields: readonly RecordField[] }) {
  return <div className={styles.fieldSection}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.infoGrid}>
      {fields.map((field) => <FieldCell field={field} key={field.label} onVisitChange={() => {}} operator="" visited={false} />)}
    </div>
  </div>;
}

export function IntentionStudentDetail() {
  const [activeTab, setActiveTab] = useState<DetailTabId>('communication');
  const [expanded, setExpanded] = useState(false);
  const [timeRange, setTimeRange] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stage, setStage] = useState('pre');
  const [includeOutbound, setIncludeOutbound] = useState(true);
  /* 沟通记录的展示态与编辑态：修改以展示值初始化表单，保存写回、取消丢弃。 */
  const [draft, setDraft] = useState<CommunicationDraft>(initialDraft);
  const [form, setForm] = useState<CommunicationDraft>(initialDraft);
  const [editing, setEditing] = useState(false);
  const [aiRecordOpen, setAiRecordOpen] = useState(false);
  const [teacherPickerOpen, setTeacherPickerOpen] = useState(false);
  const [teacherCandidate, setTeacherCandidate] = useState('');
  const [teacherQuery, setTeacherQuery] = useState('');
  const patch = (part: Partial<CommunicationDraft>) => setForm((prev) => ({ ...prev, ...part }));
  const openEdit = () => {
    setForm(draft);
    setEditing(true);
  };
  const saveEdit = () => {
    setDraft(form);
    setEditing(false);
  };

  const teacherColumns: TableColumn<(typeof receptionTeachers)[number]>[] = [
    {
      key: 'pick',
      title: '选择',
      width: 64,
      render: (teacher) => <Radio
        aria-label={`选择${teacher.label}`}
        checked={teacherCandidate === teacher.value}
        name="reception-teacher"
        onCheckedChange={(checked) => { if (checked) setTeacherCandidate(teacher.value); }}
      />,
    },
    { key: 'label', title: '老师姓名' },
  ];

  /* 学员头部信息：前两行常驻，第三行是联系与录入信息；「展开」后补基础扩展字段与两个小节。 */
  const profileFields: readonly RecordField[] = [
    { label: '微信号', value: '--' },
    { label: '身份证号', value: '--' },
    { label: '出生日期', value: '--' },
    { label: '公立学校', value: '--' },
    { label: '就读年级', value: '--' },
    { label: '就读班级', value: '--' },
    { label: 'QQ号', value: '--' },
    { label: '邮箱', value: '--' },
    { label: '企微联系人', value: '', wide: true },
    { label: '录入时间', value: `${shiftDays(demoToday, -60)} 18:32:24`, wide: true },
    { label: '录入人', value: '周予安' },
    ...(expanded ? [
      { label: '这是个自定义字段', value: '--' },
      { label: '身份证号（自）', value: '--' },
      { label: '测试删除', value: '--' },
      { label: '测试同步', value: '--' },
      { label: '市场专员', value: '周予安' },
      { label: '棘皮情况', value: '--' },
      { kind: 'edit', label: '备注', value: '' } as RecordField,
    ] : []),
  ];

  /* 展开区小节一：联系信息（家长联系方式）。 */
  const contactFields: readonly RecordField[] = [
    { label: '父亲姓名', value: '林远舟' },
    { label: '父亲电话', value: '--' },
    { label: '父亲职业', value: '--' },
    { label: '父亲身份证号', value: '--' },
    { label: '母亲姓名', value: '苏晚晴' },
    { label: '母亲电话', value: '--' },
    { label: '母亲职业', value: '--' },
    { label: '母亲身份证号', value: '--' },
    { label: '其他联系信息', value: '--' },
    { label: '家庭住址', value: '--' },
  ];

  /* 展开区小节二：招生及其他信息；主/副责任人带编辑入口。 */
  const recruitmentFields: readonly RecordField[] = [
    { label: '招生来源', value: '电子推荐卡' },
    { label: '市场渠道', value: '电子推荐卡报名', wide: true },
    { kind: 'stars', label: '意向级别', value: '2' },
    { label: '客户有效性', value: '有效' },
    { label: '例子提供者', value: '--' },
    { label: '前台接待', value: '--' },
    { label: '客户是否感兴趣', value: '--' },
    { label: '所属校区', value: '--' },
    { kind: 'edit', label: '主责任人', value: '陈清和' },
    { kind: 'edit', label: '副责任人', value: '许星禾' },
    { label: '介绍人', value: '苏念安（S08673）' },
    { label: '招生代理', value: '--' },
    { label: '预约试听时间', value: '--' },
  ];

  /* 展示态记录字段：由 draft 派生，保存编辑后这里同步更新。 */
  const displayFields: readonly RecordField[] = [
    { label: '客户状态', value: draft.customerStatus },
    { label: '客户有效性', value: draft.customerValidity },
    { kind: 'stars', label: '意向级别', value: String(draft.intentionLevel) },
    { label: '沟通情况', value: draft.situation },
    { label: '沟通类型', value: draft.commType },
    { label: '诺到访类型', value: draft.promiseVisit ? draft.visitType : '' },
    { label: '下次跟进类型', value: draft.nextFollowType },
    { label: '下次跟进日期', value: draft.nextFollowPending || !draft.nextFollowDate ? '待定' : draft.nextFollowDate },
    { full: true, label: '沟通内容', value: draft.remark },
  ];

  /* 按参考图分为开启与关闭承诺到访两种布局；隐藏字段保留草稿值。
     诺到访类型始终显示，诺到日、接待教练及校区仅在开启时显示。 */
  const editFormCard = <article aria-label="编辑沟通记录" className={[styles.recordCard, styles.editCard].filter(Boolean).join(' ')}>
    <header className={styles.recordHeader}>
      <img alt="" aria-hidden="true" className={styles.recordAvatarImg} src={teacherAvatarSrc} />
      <span className={styles.recordOperator}>{recordOperator}</span>
      <span className={styles.recordTime}>{recordTime}</span>
      <Tag category="violet" size="small">来源：AI 实时记录</Tag>
      <div className={styles.recordActions}>
        <Link leadingIcon="piyue" onClick={() => {}}>批阅</Link>
      </div>
    </header>

    <div className={styles.editPanel}>
      <span className={styles.editLabel}>客户状态:</span>
      <span className={styles.editText}>{form.customerStatus}</span>
      <span className={styles.editLabel}>客户有效性:</span>
      <span className={styles.editText}>{form.customerValidity}</span>
      <span className={styles.editLabel}>意向级别:</span>
      <EditableHearts level={form.intentionLevel} onChange={(level) => patch({ intentionLevel: level })} />

      <span className={styles.editLabel}>沟通情况:</span>
      <Select
        ariaLabel="沟通情况"
        className={styles.editControl}
        options={[
          { value: '有效沟通', label: '有效沟通' },
          { value: '无效沟通', label: '无效沟通' },
        ]}
        placeholder="请选择"
        value={form.situation}
        onValueChange={(value) => patch({ situation: value })}
      />
      <span className={styles.editLabel}>沟通类型:</span>
      <span className={styles.editText}>{form.commType}</span>
      <span className={styles.editLabel}>承诺到访:</span>
      <Switch checked={form.promiseVisit} label="承诺到访" onCheckedChange={(checked) => patch({ promiseVisit: checked })} />

      <span className={styles.editLabel}>
        {form.promiseVisit ? <span aria-hidden="true" className={styles.editRequired}>*</span> : null}
        诺到访类型:
      </span>
      <Select
        ariaLabel="诺到访类型"
        className={styles.editControl}
        options={[
          { value: '试听', label: '试听' },
          { value: '体验课', label: '体验课' },
          { value: '活动', label: '活动' },
        ]}
        placeholder="请选择"
        value={form.visitType}
        onValueChange={(value) => patch({ visitType: value })}
      />
      {form.promiseVisit ? <>
        <span className={styles.editLabel}>
          <span aria-hidden="true" className={styles.editRequired}>*</span>
          诺到日:
        </span>
        <DateTimePicker
          ariaLabel="诺到日"
          className={styles.editControl}
          value={form.visitDate}
          onValueChange={(value) => patch({ visitDate: value })}
        />
        <span className={styles.editLabel}>接待教练:</span>
        <Select
          ariaLabel="接待教练"
          className={styles.editControl}
          mode="external"
          onOpen={() => {
            setTeacherCandidate(form.receptionTeacher);
            setTeacherQuery('');
            setTeacherPickerOpen(true);
          }}
          options={receptionTeachers}
          placeholder="请选择"
          value={form.receptionTeacher}
          onValueChange={(value) => patch({ receptionTeacher: value })}
        />

      </> : null}

      <span className={styles.editLabel}>下次跟进类型:</span>
      <Select
        ariaLabel="下次跟进类型"
        className={styles.editControl}
        options={[
          { value: '电话回访', label: '电话回访' },
          { value: '上门邀约', label: '上门邀约' },
          { value: '微信跟进', label: '微信跟进' },
        ]}
        placeholder="请选择"
        value={form.nextFollowType}
        onValueChange={(value) => patch({ nextFollowType: value })}
      />
      <span className={styles.editLabel}>下次跟进日期:</span>
      <span className={styles.pendingGroup}>
        <DatePicker
          ariaLabel="下次跟进日期"
          className={styles.pendingDate}
          disabled={form.nextFollowPending}
          placeholder="请选择"
          type="date"
          value={form.nextFollowDate}
          onValueChange={(value) => patch({ nextFollowDate: value })}
        />
        <Checkbox checked={form.nextFollowPending} label="待定" onCheckedChange={(checked) => patch({ nextFollowPending: checked })} />
      </span>

      {form.promiseVisit ? <>
        <span className={styles.editLabel}>
          <span aria-hidden="true" className={styles.editRequired}>*</span>
          分配到校区:
        </span>
        <Select
          ariaLabel="分配到校区"
          className={styles.editControl}
          options={[
            { value: '岳麓校区', label: '岳麓校区' },
            { value: '湘江校区', label: '湘江校区' },
          ]}
          placeholder="请选择"
          value={form.visitCampus}
          onValueChange={(value) => patch({ visitCampus: value })}
        />
      </> : null}

      <span className={[styles.editLabel, styles.remarkLabel].join(' ')}>沟通内容:</span>
      <span className={styles.remarkBox}>
        <Textarea
          ariaLabel="沟通内容"
          className={styles.remarkInput}
          maxLength={1000}
          placeholder="请输入"
          rows={4}
          resizable={false}
          value={form.remark}
          onValueChange={(value) => patch({ remark: value })}
        />
        <span className={styles.remarkFooter}>
          <span className={styles.remarkLinks}>
            <Button className={styles.remarkAiButton} leadingIcon="sparkling-2-fill" plain onClick={() => {}}>AI识别</Button>
            <Link leadingIcon="attachment-2" onClick={() => {}}>添加附件</Link>
            <Link leadingIcon="layout-5-line" onClick={() => {}}>选择模板</Link>
          </span>
          <span className={styles.remarkCount} data-exceed={form.remark.length > 1000 || undefined}>{form.remark.length}/1000</span>
        </span>
      </span>
    </div>

    <div className={styles.editActions}>
      {/* Button 默认 type 是 primary，取消必须显式给 default，避免双主按钮。 */}
      <Button type="default" onClick={() => setEditing(false)}>取消</Button>
      <Button type="primary" onClick={saveEdit}>保存</Button>
    </div>
  </article>;

  /* 沟通记录页签的内容：筛选条 → 新增入口 → 单条记录（编辑时原位换成表单）；其余页签走占位面板。 */
  const communicationPanel = <>
    <div className={styles.filterBar}>
        <span className={styles.filterLabel}>沟通时间</span>
        <Select
          ariaLabel="沟通时间"
          className={styles.compactSelect}
          options={[
            { value: 'all', label: '不限' },
            { value: 'today', label: '今天' },
            { value: 'week', label: '最近 7 天' },
            { value: 'month', label: '最近 30 天' },
          ]}
          value={timeRange}
          onValueChange={setTimeRange}
        />
        <Input ariaLabel="沟通开始日期" className={styles.dateInput} placeholder="请选择" value={dateFrom} onValueChange={setDateFrom} />
        <span aria-hidden="true" className={styles.filterDash}>-</span>
        <Input ariaLabel="沟通结束日期" className={styles.dateInput} placeholder="请选择" value={dateTo} onValueChange={setDateTo} />
        <span className={styles.filterLabel}>沟通阶段</span>
        <Select
          ariaLabel="沟通阶段"
          className={styles.compactSelect}
          options={[
            { value: 'pre', label: '售前' },
            { value: 'post', label: '售后' },
          ]}
          value={stage}
          onValueChange={setStage}
        />
        <Checkbox checked={includeOutbound} label="包含外呼沟通记录" onCheckedChange={setIncludeOutbound} />
        <Button type="primary">查询</Button>
      </div>

      <div className={styles.addActionRow}>
        <Button type="primary">添加沟通记录</Button>
      </div>

      <div className={styles.recordList}>
        {editing ? editFormCard : <article className={styles.recordCard}>
          <header className={styles.recordHeader}>
            <img alt="" aria-hidden="true" className={styles.recordAvatarImg} src={teacherAvatarSrc} />
            <span className={styles.recordOperator}>{recordOperator}</span>
            <span className={styles.recordTime}>{recordTime}</span>
            {/* AI 来源标记：这条记录由 AI 实时记录生成。 */}
            <Tag category="violet" size="small">来源：AI 实时记录</Tag>
            <div className={styles.recordActions}>
              <Link leadingIcon="xiugai1" onClick={openEdit}>修改</Link>
              <Link leadingIcon="piyue" onClick={() => {}}>批阅</Link>
            </div>
          </header>
          <div className={styles.fieldGrid}>
            {displayFields.map((field) => (
              <FieldCell
                field={field}
                key={field.label}
                operator={recordOperator}
                showColon={false}
                visited={draft.promiseVisit}
                onVisitChange={(checked) => setDraft((prev) => ({ ...prev, promiseVisit: checked }))}
              />
            ))}
          </div>
          {/* 录音行：时长 + 下载/播放 + AI 记录详情入口，与上方字段左缘对齐。 */}
          <footer className={styles.recordFooter}>
            <span className={styles.fieldLabel}>录音时长:</span>
            <span className={styles.fieldValue}>12:24</span>
            <span className={styles.recordingActions}>
              {/* 卡内下载/播放使用本地 Icon，颜色跟随操作按钮。 */}
              <button className={styles.recordAction} type="button" onClick={() => {}}>
                <Icon name="download-line" size="sm" />
                下载
              </button>
              <button className={styles.recordAction} type="button" onClick={() => {}}>
                <Icon name="play-circle-line" size="sm" />
                播放
              </button>
              <Link className={styles.aiRecordLink} leadingIcon="aira" onClick={() => setAiRecordOpen(true)}>
                <span className={styles.aiRecordText}>查看 AI 记录</span>
              </Link>
            </span>
          </footer>
        </article>}
      </div>
  </>;

  return <div className={styles.page}>
    <div className={styles.backRow}>
      {/* 后期上一层接入学员列表汇总页后，这里改为跳列表页。 */}
      <Link leadingIcon="a-arrow-left" onClick={() => window.history.back()}>返回</Link>
      <span aria-hidden="true" className={styles.backDivider} />
      <PageBreadcrumb current="意向学员管理" items={['招生营销']} />
    </div>

    <article aria-label="学员基本信息" className={styles.card}>
      <div className={styles.profileCard}>
        {/* 默认头像 95×133，有真实头像后同尺寸替换。 */}
        <img alt="" className={styles.avatar} src={defaultAvatarSrc} />
        <div className={styles.profileMain}>
          <div className={styles.profileTitleRow}>
            <h1 className={styles.studentName}>林知夏</h1>
            <span className={styles.studentPhone}>17621287991</span>
            {/* 拨号按钮使用本地电话图标。 */}
            <button aria-label="拨打电话" className={styles.dialButton} type="button" onClick={() => {}}>
              <Icon name="dianhua2" size="xs" />
            </button>
            {/* 操作按钮与名字同行（右贴），视觉上同一水平线。 */}
            <div className={styles.profileActions}>
              <Button type="primary">收费</Button>
              <Button type="default">修改</Button>
              <Button type="default">转为已回收客户</Button>
              {/* 信息提示按规范用 i（非 ?/!），优先线性。 */}
              <Button trailingIcon="information-2-line" type="default">转为正式学员</Button>
            </div>
          </div>
          <div className={styles.profileTagRow}>
            <span className={styles.fieldLabel}>客户状态:</span>
            <Tag type="warning">需求强烈</Tag>
            <span className={styles.fieldLabel}>客户标签:</span>
            <Link onClick={() => {}}>添加标签</Link>
            <Link className={styles.expandLink} onClick={() => setExpanded(!expanded)} trailingIcon={expanded ? 'caret-top' : 'caret-bottom'}>
              {expanded ? '收起' : '展开'}
            </Link>
          </div>
          <div className={styles.infoWrap}>
            <div className={styles.infoGrid}>
              {profileFields.map((field) => field.label === '企微联系人'
                ? <div className={[styles.field, styles.fieldWide].filter(Boolean).join(' ')} key={field.label}>
                  {/* 标题 + 提示图标：说明收进悬停气泡（共享 FieldHelp），值位空。 */}
                  <span className={[styles.fieldLabel, styles.helpLabel].join(' ')}>
                    <span>企微联系人</span>
                    <FieldHelp content="仅支持在企业微信上进行关联" label="企微联系人说明" />
                    <span>:</span>
                  </span>
                  <span className={[styles.fieldValue, styles.fieldValueEmpty].filter(Boolean).join(' ')}>--</span>
                </div>
                : <FieldCell field={field} key={field.label} onVisitChange={() => {}} operator="林知夏" visited={false} />)}
            </div>
          </div>
          {expanded ? <FieldSection fields={contactFields} title="联系信息" /> : null}
          {expanded ? <FieldSection fields={recruitmentFields} title="招生及其他信息" /> : null}
        </div>
      </div>
    </article>

    {/* 页签走 ModulePage 的 PageTabs，与其他功能页的主页签同一高度与样式。 */}
    <article aria-label="学员详情内容" className={styles.tabsCard}>
      <PageTabs
        ariaLabel="意向学员详情页签"
        onValueChange={(id) => setActiveTab(id as DetailTabId)}
        panelClassName={styles.tabPanel}
        tabs={detailTabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          panel: tab.id === 'communication' ? communicationPanel : <div className={styles.emptyPanel}>该页签内容建设中</div>,
        }))}
        value={activeTab}
      />
    </article>

    <Dialog
      open={teacherPickerOpen}
      onOpenChange={setTeacherPickerOpen}
      title="选择接待教练"
      headerTone="brand"
      width={640}
      footer={<div className={styles.teacherPickerFooter}>
        <span className={styles.teacherPickerSummary}>{teacherCandidate ? `已选择：${teacherCandidate}` : '请选择一位老师'}</span>
        <Button type="default" onClick={() => setTeacherPickerOpen(false)}>取消</Button>
        <Button disabled={!teacherCandidate} onClick={() => {
          patch({ receptionTeacher: teacherCandidate });
          setTeacherPickerOpen(false);
        }}>确定</Button>
      </div>}
    >
      <Input
        ariaLabel="搜索老师姓名"
        allowClear
        className={styles.teacherPickerSearch}
        placeholder="搜索老师姓名"
        value={teacherQuery}
        onValueChange={setTeacherQuery}
      />
      <div className={styles.teacherPickerTable}>
        <Table
          ariaLabel="可选接待老师"
          columns={teacherColumns}
          currentKey={teacherCandidate}
          emptyText="没有符合条件的老师"
          onRowClick={(teacher) => setTeacherCandidate(teacher.value)}
          rowKey={(teacher) => teacher.value}
          rows={receptionTeachers.filter((teacher) => teacher.label.includes(teacherQuery.trim()))}
        />
      </div>
    </Dialog>

    {/* AI 记录详情弹窗：记录标题与元信息和沟通记录卡同源。 */}
    <AiRecordDialog
      onOpenChange={setAiRecordOpen}
      open={aiRecordOpen}
      operator={recordOperator}
      recordTitle="林知夏的沟通记录"
      studentName="林知夏"
      time={recordTime}
    />
  </div>;
}
